/**
 * `<InterviewPrepTab>` — orchestrator for the Interview Prep tab content
 * (Batch 30j-4, Phase 2.6, Phase 2.6.1 HoD fix-it, durable rev 2026-06-17).
 *
 * Owns the state machine:
 *   - phase derivation via `derivePrepPhase` (lib/derivePrepPhase.ts)
 *   - composition of all visual states + no-jd / error branches
 *   - per-stage chrome via `<PrepActiveStageHeader>` (Phase 2.6 §3.1)
 *
 * Regenerate removed (auto-eval-prep): the Interview Prep artifact is
 * deterministic. Once generated, no re-run affordance is surfaced.
 * The `prep-generate` testid is only present in the empty state (not
 * in the loaded state). The old "always present when not streaming"
 * doc-comment claim is now superseded by this note.
 *
 * Spec: `frontend/docs/30j_4_design.md` §3 + §4 +
 *       `design/post-walkthrough/interview-prep-spec.md` §3 / §6.
 *
 * Durable rev (2026-06-17): Interview Prep was re-architected off the SSE
 * stream onto the durable job_queue worker. The `useSseStream` consumer
 * wiring (EventSource, `shouldStream`/`activeStreamId` state, the AR-7
 * tab-switch reconciliation effect) is GONE. Generation is now poll-driven:
 *   1. Generate click → `useStartInterviewPrep` POST returns `{id, status:'queued'}`
 *      and optimistically writes a queued row to cache.
 *   2. `useInterviewPrep` POLLS every 3s while status ∈ {queued, running};
 *      when the worker persists `complete`, the poll lands the canonical row
 *      and the phase flips to `loaded`. Navigating away no longer kills it.
 *
 * LCP guarantee (Sec HARD-1 / S-30j4-3 / S-30j4-17): the `useInterviewPrep`
 * query is gated on `activeTab === "prep"`.
 *
 * Phase 2.6.1 — HoD milestone-gate fix-it deltas (unchanged):
 *   - B1: `<PrepActiveStageHeader>` is the canonical stage chrome on
 *         loaded + generating. Empty-state copy references stage name.
 *   - B2: Generating state renders `<PrepSplitPanelSkeleton>` + the shared
 *         `<EvaluationProgressBanner>`, NOT live token markdown. The final
 *         markdown swaps in only after the worker completes.
 *   - B3: Single GENERATING indicator. Header action button HIDDEN while
 *         generating; the banner is the sole status surface.
 *   - B4: `<EvaluationProgressBanner>` reused with `phases` + `headline`.
 *         A sibling PR adds a "runs in background" reassurance line to that
 *         shared banner — inherited here, no change needed.
 *
 * NO URL state for active section (HoD-30j4-11 / S-30j4-18).
 * NO auto-scroll (HoD-30j4-12 / S-30j4-19).
 * NO fork of `<EvaluationMarkdown>` (HoD-30j4-2 / S-30j4-10).
 * NO bespoke generating pill (Phase 2.6.1 B4 — reuse banner).
 */
import {
  useCallback,
  useMemo,
  type JSX,
  type ReactNode,
} from "react";
import { AlertTriangle, Briefcase, Sparkles } from "lucide-react";

import { Button } from "@/components/common/Button";

import { EvaluationProgressBanner } from "@/components/candidate-page/EvaluationProgressBanner";
import { PrepActiveStageHeader } from "@/components/candidate-page/PrepActiveStageHeader";
import { PrepSplitPanel } from "@/components/candidate-page/PrepSplitPanel";
import { PrepSplitPanelSkeleton } from "@/components/candidate-page/PrepSplitPanelSkeleton";
import type { TabKey } from "@/components/candidate-page/tabs";

import { useInterviewPrep } from "@/hooks/queries/interview-prep/useInterviewPrep";
import { useStartInterviewPrep } from "@/hooks/queries/interview-prep/useStartInterviewPrep";
import { useInterviewPrepHistory } from "@/hooks/queries/interview-prep/useInterviewPrepHistory";
import { useTenantPipelineStages } from "@/hooks/queries/candidates/useTenantPipelineStages";
import type { Candidate } from "@/hooks/queries/candidates/useCandidates";
import { ApiError } from "@/lib/apiError";
import { parsePrep } from "@/lib/parsePrep";
import { derivePrepPhase } from "@/lib/derivePrepPhase";
import { PrepHistoryAccordion } from "@/components/candidate-page/PrepHistoryAccordion";
import { PrepProvenanceLine } from "@/components/candidate-page/PrepProvenanceLine";
import { AiRatingWidget } from "@/components/ai/AiRatingWidget";

/**
 * Cycling phase strings shown beneath the banner headline during prep
 * generation. Spec §3.3 enumerates these four phases.
 */
const PREP_PHASES = [
  "Reading job + resume…",
  "Reviewing prior analyses…",
  "Drafting prep guide…",
  "Adding follow-ups…",
] as const;

interface JdSummary {
  id: string;
  name: string;
}

interface InterviewPrepTabProps {
  candidate: Candidate;
  jdId: string | undefined;
  jds: JdSummary[];
  activeTab: TabKey;
}

export function InterviewPrepTab({
  candidate,
  jdId,
  jds,
  activeTab,
}: InterviewPrepTabProps): JSX.Element {
  // Existing-prep query — gated on tab activation. Polls while the worker
  // is generating (queued/running) and stops on complete/failed.
  const prepQ = useInterviewPrep({
    candidateId: candidate.id,
    jdId,
    enabled: activeTab === "prep",
  });

  // Phase 2.6 — per-stage prep history accordion (§5).
  const historyQ = useInterviewPrepHistory({
    candidateId: candidate.id,
    jdId,
    enabled: activeTab === "prep",
  });

  // Phase 2.6 — resolve the active pipeline stage for the candidate so
  // POST /interview-prep can stamp stage_id. Terminal stages are
  // explicitly excluded — the backend will 422 in that case but we
  // skip the call entirely.
  const stagesQ = useTenantPipelineStages();
  const activeStage = useMemo(() => {
    if (!stagesQ.data) return null;
    const slug = candidate.status;
    if (!slug) return null;
    const stage = stagesQ.data.find((s) => s.slug === slug);
    if (!stage || stage.is_terminal) return null;
    return stage;
  }, [stagesQ.data, candidate.status]);

  // The matched pipeline stage IS terminal (e.g. `hired`). This is the
  // reason `activeStage` is null for terminal candidates — NOT a still-in-
  // flight stages query. We surface accurate copy for these candidates
  // instead of the "Resolving active stage…" loading affordance, which
  // would otherwise appear stuck forever (terminal candidates legitimately
  // have no active stage by design).
  const terminalStage = useMemo(() => {
    if (!stagesQ.data) return null;
    const slug = candidate.status;
    if (!slug) return null;
    const stage = stagesQ.data.find((s) => s.slug === slug);
    return stage && stage.is_terminal ? stage : null;
  }, [stagesQ.data, candidate.status]);

  const startMutation = useStartInterviewPrep();

  const phase = derivePrepPhase({ jdId, query: prepQ });
  const row = prepQ.data ?? null;

  // Memoize parser invocation on the FINALIZED markdown only —
  // NOT on streaming chunks (Architect §8.1). Re-runs only when
  // row.content changes (initial cache hit OR post-stream invalidation).
  const parsed = useMemo(
    () => (row?.content ? parsePrep(row.content) : null),
    [row?.content],
  );

  // Derived JD context.
  const jdName = jds.find((j) => j.id === jdId)?.name;

  // "Generating" surface = the worker is producing the guide. Driven by the
  // optimistic/polled row status (phase === "streaming-active") plus the brief
  // window while the POST mutation is in flight before the optimistic write.
  const isStreaming = startMutation.isPending || phase === "streaming-active";
  const hasExisting = phase === "loaded";

  const onGenerate = useCallback((): void => {
    if (!jdId) return;
    startMutation.mutate(
      {
        candidate_id: candidate.id,
        jd_id: jdId,
        // Phase 2.6 — pass stage_id when we can resolve a non-terminal
        // active stage. Backend renders chain enrichment using the
        // prior stages' analyses. When unresolved (loading / terminal /
        // no JD-pipeline match), legacy un-staged behaviour applies.
        ...(activeStage ? { stage_id: activeStage.id } : {}),
      },
      {
        onError: (err) => {
          // 409 attach-to-existing: the hook already toasted + invalidated
          // the by-resource query, so the poll will pick up the in-flight
          // row and the phase flips to generating. Nothing to do here.
          // Other errors bubble; phase derivation may surface via prepQ.
          if (
            err instanceof ApiError &&
            err.error === "action_already_running"
          ) {
            return;
          }
        },
      },
    );
  }, [candidate.id, jdId, startMutation, activeStage]);

  // History rows for past stages — exclude the current visible prep row
  // (it lives in the active section above the accordion).
  const historyRows = useMemo(() => {
    const rows = historyQ.data ?? [];
    const activeId = row?.id;
    return rows.filter((r) => r.id !== activeId);
  }, [historyQ.data, row?.id]);

  // Provenance line for the active prep — count of prior analyses for
  // this (candidate, jd) excluding the current row. Cheap proxy; the
  // backend doesn't return per-prep provenance in v1 history rows.
  // Empty-chain (no prior preps) → "from job + resume only" copy.
  const activeProvenance = useMemo(() => {
    if (phase !== "loaded" || !row) return null;
    const priorCount = historyRows.length;
    const sources =
      priorCount === 0
        ? []
        : [
            priorCount === 1
              ? "1 prior analysis"
              : `${priorCount} prior analyses`,
          ];
    return (
      <PrepProvenanceLine
        generatedAt={row.completed_at ?? row.created_at}
        sources={sources}
      />
    );
  }, [phase, row, historyRows.length]);

  // Resolve the canonical stage-name string the header + empty state
  // both read. Falls back to the saved row's jd-stage if `activeStage`
  // is null (terminal / loading / unresolved tenant-pipeline match).
  const stageName = activeStage?.name ?? null;
  const stageSlug = activeStage?.slug ?? null;
  const firstName = candidate.name.split(" ")[0] ?? candidate.name;
  const priorAnalysisCount = historyRows.length;

  // Render the canonical action button. Hidden entirely during streaming
  // (B3 — banner is the single status indicator; spec §3.3), when a prep
  // already exists (`hasExisting` — artifact is deterministic, no re-run
  // affordance; auto-eval-prep change), AND while the existing-prep query is
  // still settling (`loading-existing`): during loading we don't yet know
  // whether a prep exists, so surfacing Generate would flash it for a frame
  // before existing content lands (founder bug FIX 1).
  // The `prep-generate` testid is present in the empty state only.
  function renderAction(): ReactNode {
    if (isStreaming || hasExisting || phase === "loading-existing") return null;
    return (
      <Button
        variant="primary"
        onClick={onGenerate}
        disabled={!jdId}
        data-testid="prep-generate"
        className="px-3 py-1.5 text-sm"
      >
        <span className="inline-flex items-center gap-1.5">
          <Sparkles size={14} aria-hidden />
          Generate Prep Guide
        </span>
      </Button>
    );
  }

  // Header strip — `<PrepActiveStageHeader>` when we have a resolvable
  // stage; otherwise a lightweight title strip for fallback states
  // (no-jd, terminal-or-unresolved-stage). The action button renders
  // under both paths; callers can rely on the `prep-generate` testid
  // being present in the empty state.
  function renderHeader(): ReactNode {
    if (!stageName || !stageSlug) {
      // Defensive fallback — stages haven't loaded yet OR the candidate
      // is in a terminal/unknown slug OR the candidate has no JD context.
      // Keep the action surface visible so the recruiter isn't stranded.
      return (
        <div className="border-b border-border-default px-content-px py-3.5">
          <header
            className="flex flex-wrap items-center justify-between gap-3"
            data-testid="interview-prep-topbar-fallback"
          >
            <div className="flex flex-col gap-0.5">
              <h2 className="text-sm font-semibold text-text-primary">
                Interview Prep
              </h2>
              <p className="text-xs text-text-tertiary">
                {!jdName
                  ? "Pick a job description to view or generate a prep guide."
                  : terminalStage
                    ? `${terminalStage.name} — no active interview stage.`
                    : !stagesQ.data
                      ? "Resolving active stage…"
                      : "No active interview stage for this candidate."}
              </p>
            </div>
            <div className="flex items-center gap-2">{renderAction()}</div>
          </header>
        </div>
      );
    }
    return (
      <div className="border-b border-border-default px-content-px pt-3.5 pb-3.5">
        <PrepActiveStageHeader
          stageName={stageName}
          stageSlug={stageSlug}
          roleName={jdName ?? null}
          provenance={
            phase === "loaded" && activeProvenance ? activeProvenance : null
          }
          actions={renderAction()}
        />
      </div>
    );
  }

  return (
    <div
      className="flex flex-1 flex-col overflow-hidden"
      data-testid="interview-prep-tab"
    >
      {renderHeader()}
      <div
        className="scroll-primary flex flex-1 flex-col overflow-x-hidden overflow-y-auto"
        data-testid="prep-content"
      >
        {phase === "no-jd" ? <NoJdEmptyState /> : null}
        {phase === "loading-existing" ? <PrepSplitPanelSkeleton /> : null}
        {phase === "empty" ? (
          <EmptyState
            jdName={jdName}
            stageName={stageName}
            firstName={firstName}
            priorAnalysisCount={priorAnalysisCount}
            canGenerate={!!jdId}
            onGenerate={onGenerate}
          />
        ) : null}
        {phase === "streaming-active" ? (
          <StreamingBuffered
            candidateName={candidate.name}
            stageName={stageName ?? jdName ?? "this stage"}
            firstName={firstName}
          />
        ) : null}
        {phase === "loaded" && parsed ? (
          <>
            <PrepSplitPanel parsed={parsed} />
            {/* B-12: AI rating widget — after PrepSplitPanel, before PrepHistoryAccordion */}
            <div className="flex justify-end px-content-px pb-1 pt-1">
              <AiRatingWidget
                actionType="interview_prep"
                resourceKey={row ? `interview_prep:${row.id}` : ""}
                modelUsed={row?.model_used ?? null}
              />
            </div>
            {historyRows.length > 0 ? (
              <div className="px-content-px">
                <PrepHistoryAccordion
                  rows={historyRows}
                />
              </div>
            ) : null}
          </>
        ) : null}
        {phase === "error" ? (
          <PrepErrorState onRetry={onGenerate} canRetry={!!jdId} />
        ) : null}
      </div>
    </div>
  );
}

// ── State surfaces ──────────────────────────────────────────────────────────

function NoJdEmptyState(): JSX.Element {
  return (
    <CenteredEmpty data-testid="prep-no-jd">
      <Briefcase size={48} className="mb-4 text-text-tertiary" aria-hidden />
      <h3 className="mb-1.5 text-base font-semibold text-text-primary">
        No job context
      </h3>
      <p className="max-w-md text-sm text-text-secondary">
        Open this candidate from a job pipeline to generate interview prep.
      </p>
    </CenteredEmpty>
  );
}

interface EmptyStateProps {
  jdName: string | undefined;
  stageName: string | null;
  firstName: string;
  priorAnalysisCount: number;
  canGenerate: boolean;
  onGenerate: () => void;
}

function EmptyState({
  jdName,
  stageName,
  firstName,
  priorAnalysisCount,
  canGenerate,
  onGenerate,
}: EmptyStateProps): JSX.Element {
  // Spec §6.2 — role-named headline + conditional sub-body.
  // Use the JD/role name as the primary display; fall back to stage name only
  // when no JD context is available. Never show a raw status label ("Active").
  const displayName = jdName ?? stageName;
  const headline = displayName
    ? `Prep for ${displayName}`
    : "No Prep Guide Yet";
  const bodyLead = displayName
    ? `Generate an AI prep guide for ${firstName}'s interview for ${displayName}.`
    : `Generate an interview prep guide tailored to this role.`;
  const subBodyLead =
    priorAnalysisCount === 0
      ? `This prep will use the job description and ${firstName}'s resume.`
      : priorAnalysisCount === 1
        ? "This prep will be enriched by 1 prior interview analysis."
        : `This prep will be enriched by ${priorAnalysisCount} prior interview analyses.`;
  // Durable rev: the worker runs out of band, so reassure the recruiter they
  // can navigate away while it generates.
  const subBody = `${subBodyLead} Takes up to a minute, and runs in the background.`;

  return (
    <CenteredEmpty data-testid="prep-empty">
      <Sparkles size={48} className="mb-4 text-text-tertiary" aria-hidden />
      <h3 className="mb-1.5 text-base font-semibold text-text-primary">
        {headline}
      </h3>
      <p className="mb-2 max-w-md text-sm text-text-secondary">{bodyLead}</p>
      <p className="mb-5 max-w-md text-xs text-text-tertiary">{subBody}</p>
      <Button
        variant="primary"
        onClick={onGenerate}
        disabled={!canGenerate}
        data-testid="prep-empty-generate"
      >
        <span className="inline-flex items-center gap-2">
          <Sparkles size={16} aria-hidden />
          Generate Prep Guide
        </span>
      </Button>
    </CenteredEmpty>
  );
}

interface StreamingBufferedProps {
  candidateName: string;
  stageName: string;
  firstName: string;
}

/**
 * Generating surface (B2). Spec §3.2: "the prep content area shows a loading
 * skeleton + a single status banner (NOT a token-by-token live render)."
 * The skeleton stays mounted while the worker generates; when the poll lands
 * the persisted row, phase derivation flips to `loaded` and the parent swaps
 * atomically to `<PrepSplitPanel>`.
 *
 * Durable rev (2026-06-17): there is no SSE stream / chunk buffer anymore —
 * the worker runs out of band and the FE polls. The surface is purely the
 * skeleton + banner, which is exactly what recruiters already saw (live
 * tokens were always suppressed). The `prep-streaming-active` testid is kept
 * so existing baselines + `waitForPrepPhase` helpers keep working.
 */
function StreamingBuffered({
  candidateName,
  stageName,
  firstName,
}: StreamingBufferedProps): JSX.Element {
  return (
    <div data-testid="prep-streaming" data-phase="streaming-active">
      <div data-testid="prep-streaming-active" className="contents">
        <div className="px-content-px pt-5">
          <EvaluationProgressBanner
            candidateName={candidateName}
            jdName={stageName}
            phases={PREP_PHASES}
            headline={`Drafting prep for ${firstName}'s ${stageName}…`}
          />
        </div>
        <PrepSplitPanelSkeleton />
      </div>
    </div>
  );
}

interface PrepErrorStateProps {
  onRetry: () => void;
  canRetry: boolean;
}

function PrepErrorState({
  onRetry,
  canRetry,
}: PrepErrorStateProps): JSX.Element {
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center px-content-px py-6 text-center"
      data-testid="prep-error"
    >
      <div className="flex max-w-md items-start gap-3 rounded-md border border-status-rejected-ring bg-status-rejected-bg p-3.5 text-status-rejected-fg">
        <AlertTriangle
          size={16}
          className="mt-0.5 flex-shrink-0"
          aria-hidden
        />
        <div className="text-left">
          <p className="mb-0.5 text-sm font-semibold">
            Generation interrupted
          </p>
          <p className="text-xs">
            The prep stream ended before completing. Retry, or contact
            support if it persists.
          </p>
          <button
            type="button"
            onClick={onRetry}
            disabled={!canRetry}
            className="mt-2 text-xs underline underline-offset-2 hover:no-underline disabled:opacity-50"
            data-testid="prep-error-retry"
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helper ──────────────────────────────────────────────────────────────────

function CenteredEmpty({
  children,
  ...rest
}: { children: ReactNode } & Record<string, unknown>): JSX.Element {
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center px-content-px py-6 text-center"
      {...rest}
    >
      {children}
    </div>
  );
}
